import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneArt } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import {
  ContactSection,
  SocialSection,
  GeoSection,
  VisibilitySection,
  DescriptionSection,
} from "../utils/AddSections";
import { FiImage, FiFeather, FiDollarSign } from "react-icons/fi";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const art: any = getOneArt();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const art_id = art?.art_id;
  const [title, setTitle] = useState<any>(art?.title);
  const [thumbnail, setThumbnail] = useState<any>(art?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(art?.image_cdn_id);
  const [email, setEmail] = useState<any>(art?.email_address);
  const [phoneNumber, setPhoneNumber] = useState<any>(art?.phone_number);
  const [whatsapp, setWhatsapp] = useState<any>(art?.whatsapp);
  const [facebook, setFacebook] = useState<any>(art?.facebook);
  const [twitter, setTwitter] = useState<any>(art?.twitter);
  const [instagram, setInstagram] = useState<any>(art?.instagram);
  const [youtube, setYoutube] = useState<any>(art?.youtube);
  const [hashtags, setHashtags] = useState<any>(art?.hashtag);
  const [googleMapCode, setGoogleMapCode] = useState<any>(art?.google_map_code);
  const [latitude, setLatitude] = useState<any>(art?.latitude);
  const [longitude, setLongitude] = useState<any>(art?.longitude);
  const [formattedAddress, setFormattedAddress] = useState<any>(
    art?.formatted_address
  );
  const [featured, setFeatured] = useState<any>(art?.featured);
  const [promoted, setPromoted] = useState<any>(
    art?.promoted ? art?.promoted : "no"
  );
  const [artStatus, setArtStatus] = useState<any>(art?.status);
  const [expo, setExpo] = useState<any>(art?.expo);
  const [description, setDescription] = useState<any>(art?.description);
  const [pricing, setPricing] = useState<any>(art?.Pricings);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);
  const [payLink, setPayLink] = useState<any>(art?.pay_link);

  const artRoutes = "/data/art";

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneArtHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : art?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title?.toLowerCase().replace(/\s/g, "_"),
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      phone_number: phoneNumber,
      email_address: email,
      whatsapp: whatsapp,
      facebook: facebook,
      twitter: twitter,
      instagram: instagram,
      youtube: youtube,
      hashtag: hashtags,
      audio_adv: "none",
      google_map_code: googleMapCode,
      latitude: latitude,
      longitude: longitude,
      formatted_address: formattedAddress,
      country_short: "none",
      status: artStatus,
      featured,
      promoted,
      expo,
      pricing,
      pay_link: payLink,
    };

    const { message, status } = await updateOneContent({
      data_id: art_id,
      data: data,
      content: _TYPES_.ART,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={artRoutes}
      eyebrow="DoSee Data • Art"
      title="Edit Art"
      subtitle={`Update an art piece, gallery or studio in ${entry_label || "this destination"}.`}
      icon={<FiFeather className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneArtHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="A high-quality photo of the work or space."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.ART)}
        />
      </AddSection>

      <AddSection title="Basics" hint="Title and address.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              value: formattedAddress || "",
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <ContactSection
        phone={phoneNumber}
        setPhone={setPhoneNumber}
        email={email}
        setEmail={setEmail}
        payLink={payLink}
        setPayLink={setPayLink}
        clearMessages={clearMessages}
      />

      <SocialSection
        whatsapp={whatsapp}
        setWhatsapp={setWhatsapp}
        facebook={facebook}
        setFacebook={setFacebook}
        twitter={twitter}
        setTwitter={setTwitter}
        instagram={instagram}
        setInstagram={setInstagram}
        youtube={youtube}
        setYoutube={setYoutube}
        clearMessages={clearMessages}
      />

      <GeoSection
        googleMapCode={googleMapCode}
        setGoogleMapCode={setGoogleMapCode}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        clearMessages={clearMessages}
      />

      <VisibilitySection
        statusId="art_status"
        setStatus={setArtStatus}
        setFeatured={setFeatured}
        setPromoted={setPromoted}
        setExpo={setExpo}
        status={artStatus}
        featured={featured}
        promoted={promoted}
        expo={expo}
        clearMessages={clearMessages}
      />

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
